/**
 * What the composer shows while a song is generating.
 *
 * The prototype showed nothing at all: the button read "Generating…" and the rest of
 * the screen sat still. This gives that wait a place on the page without inventing
 * detail the app does not have.
 *
 * It is now a thin wrapper over ProcessingWave, which is this component generalised —
 * the Genre Mixer and Music DNA wait for the same kind of thing, and three screens each
 * with their own loader is how a consistent processing state stops being consistent.
 * What survives here is the copy: "Creating your song" is product wording asserted by
 * the Create flow tests, and it belongs to the composer rather than to the shared
 * component. Nothing about how this looks or reads has changed.
 *
 * SIMULATED, and honest about it — see ProcessingWave for the argument in full. There is
 * no queue position and no narrated stage list ("analysing harmony…"): it states the one
 * true thing and animates a placeholder waveform.
 *
 * `progress` IS A REAL PROP NOW, AND EVERY CALLER LEAVES IT ALONE. The generation flow
 * reports progress through `GenerationJob.progress` (api/generationApi — `POST /generations`
 * then poll `GET /generations/:id`), and the mock has none to report: it is 0 until the job
 * completes and 100 when it does, because interpolating between those would be reporting
 * elapsed time as work completed. So the default is `null`, no bar is rendered, and this
 * screen says exactly what it said before.
 *
 * What changed is that the pass-through the previous version of this note only PROMISED now
 * exists: when a backend reports genuine progress, the value reaches ProcessingWave's
 * ui/ProgressBar through this prop and the surrounding layout does not move.
 */
import ProcessingWave from './ProcessingWave';

export interface GenerationStatusProps {
  /**
   * Reported generation progress 0–100, or `null` when nothing is reporting any.
   *
   * See ProcessingWave's own `progress` note for why absent is a real answer and why `null`
   * has to be said rather than implied.
   */
  progress?: number | null;
}

export default function GenerationStatus({ progress = null }: GenerationStatusProps = {}) {
  return (
    <ProcessingWave
      title="Creating your song"
      detail="This usually takes about a minute or two. Please hold."
      // Seeded from a fixed string rather than a song id — there is no song yet.
      seed="Lyrixa-generating"
      progress={progress}
      style={{ marginTop: 20 }}
    />
  );
}
